import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ProfileHeader from '../components/ProfileHeader';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { useSelectedOrganizationContext } from '../utils/organizationSelection';
import {
  buildClientProgressSummary,
  buildClientNextStepRecommendations,
  buildClientWorkspace,
  buildFollowUpDraftPrompt,
  buildInstructorRetentionQueues,
  buildPackageOfferCoachingSummary,
  buildOrganizationRetentionQueues,
  buildPackageOfferDraftPrompt,
  buildRebookCoachingSummary,
  buildRebookSuggestionExplanation,
  createPackageOfferSuggestion,
  createRebookRequestFromLastService,
  getClientsAtRiskOfDropoff,
  getFollowUpEffectivenessSummary,
  getInstructorConversionFunnel,
  getInstructorRetentionSummary,
  getOrganizationClientProfiles,
  getOrganizationLessons,
  getServiceConversionFunnel,
  getServiceRetentionSummary,
  normalizeClientProfile,
} from '../utils/clientCRM';
import {
  buildFollowUpDraftMessage,
  buildPackageOfferDraftMessage,
  bulkAddClientNote,
  buildBulkFollowUpDrafts,
  bulkMarkReviewed,
  bulkAssignInstructor,
  bulkSnoozeClients,
  filterRetentionQueue,
  getBulkActionPreview,
  getFullConversionFunnel,
  getConversionByInstructor,
  getConversionByService,
  getConversionByTrafficSource,
  getFollowUpConversionRate,
  getOrganizationRetentionActions,
  getPackageOfferConversionRate,
  getRetentionRevenueSummary,
  getRetentionActionSummary,
  getRetentionQueueFilterOptions,
  logRetentionAction,
  openThreadWithDraft,
  performBulkRetentionAction,
} from '../utils/retentionActions';
import {
  getInstructorBookings,
  getInstructorBookingRequests,
  getOrganizationBookings,
  getOrganizationBookingRequests,
} from '../utils/booking';
import {
  getOrganizationCustomerMemberships,
  getOrganizationServicePackages,
} from '../utils/servicePackages';
import { getOrganizationServices } from '../utils/services';
import {
  canManageBulkRetentionActions,
  canManageBulkClientNotes,
  canManageBulkInstructorReassignment,
  canManageRetentionActions,
  canSnoozeRetentionRecommendation,
  canUseAIAssistDrafts,
  canViewRetentionReporting,
  canViewConversionFunnels,
  canViewRetentionWorkspace,
} from '../utils/platformAccess';
import AiCopilotPanel from '../components/ai/copilot/AiCopilotPanel';
import { buildClientSignalsFromWorkspace } from '../utils/clientSignals';
import { buildOrganizationRetentionOpportunities } from '../utils/retentionOpportunity';

const QUEUE_CONFIG = {
  all: { label: 'All', key: 'all', description: 'A practical retention workspace across current follow-up queues.' },
  rebook: { label: 'Needs Rebook', key: 'needingRebook', description: 'Clients who look ready for a next session.' },
  credits: { label: 'Unused Credits', key: 'unusedCredits', description: 'Clients with prepaid value still available.' },
  expiring: { label: 'Expiring', key: 'expiringMemberships', description: 'Memberships or packages expiring soon.' },
  inactive: { label: 'Inactive', key: 'inactiveClients', description: 'Clients drifting quiet based on richer activity signals.' },
  follow_up: { label: 'Follow-Up', key: 'followUpRecommended', description: 'Clients flagged for a coach-led follow-up.' },
  feedback_ready: { label: 'Feedback Ready', key: 'feedbackReadyNoBooking', description: 'Feedback is ready, but no next session is booked.' },
  package_balance: { label: 'Package Balance', key: 'packageBalanceNoUpcoming', description: 'Prepaid balance remains without an upcoming session.' },
};

const EMPTY_RETENTION_QUEUES = {
  needingRebook: [],
  unusedCredits: [],
  expiringMemberships: [],
  inactiveClients: [],
  feedbackReadyNoBooking: [],
  followUpRecommended: [],
  packageBalanceNoUpcoming: [],
};

function formatDate(value) {
  if (!value) return 'Not yet';
  if (typeof value?.toDate === 'function') return value.toDate().toLocaleString();
  if (value?.seconds) return new Date(value.seconds * 1000).toLocaleString();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not yet' : parsed.toLocaleString();
}

// Pass 80 §3/§30 — the canonical commercial identity is `clientId`. A legacy profile keyed only by an Auth uid
// falls back to that uid so the queue still functions, but a real commercial clientId always wins.
function clientIdOfProfile(profile = {}) {
  return String(profile.clientId || profile.userId || '').trim();
}

function getQueueEntries(queueType = 'all', queues = {}) {
  if (queueType === 'all') {
    return Object.entries(QUEUE_CONFIG)
      .filter(([key]) => key !== 'all')
      .flatMap(([key, config]) => (queues[config.key] || []).map((profile) => ({
        queueType: key,
        profile,
      })));
  }

  const config = QUEUE_CONFIG[queueType] || QUEUE_CONFIG.all;
  return (queues[config.key] || []).map((profile) => ({
    queueType,
    profile,
  }));
}

function RetentionQueuePage() {
  const navigate = useNavigate();
  const { queueType = 'all' } = useParams() || {};
  const { currentUser, userData, viewMode } = useAuth() || {};
  const { selectedOrganizationId, selectedOrganization } = useSelectedOrganizationContext(userData) || {};
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [clientProfiles, setClientProfiles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [servicePackages, setServicePackages] = useState([]);
  const [services, setServices] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [timelineItemsByUserId, setTimelineItemsByUserId] = useState({});
  const [feedbackHistoryByUserId, setFeedbackHistoryByUserId] = useState({});
  const [lessonsByUserId, setLessonsByUserId] = useState({});
  const [clientWorkspacesByUserId, setClientWorkspacesByUserId] = useState({});
  const [draftsByUserId, setDraftsByUserId] = useState({});
  const [retentionActions, setRetentionActions] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  // { profile, signals } — signals pre-built so AiCopilotPanel gets them ready-made
  const [aiFollowUpModal, setAiFollowUpModal] = useState(null);
  const [bulkInstructorId, setBulkInstructorId] = useState('');
  const [bulkNoteText, setBulkNoteText] = useState('');
  const [filters, setFilters] = useState({
    instructorId: '',
    serviceId: '',
    packageStatus: '',
    membershipStatus: '',
    riskLevel: '',
    lastActivityWindow: '',
    discipline: '',
    locationType: '',
  });

  const organizationId = selectedOrganizationId || userData?.orgId || '';
  const isInstructorScoped = userData?.role === 'instructor' && viewMode === 'instructor';

  const loadWorkspace = useCallback(async () => {
    if (!organizationId || !currentUser?.uid) {
      setClientProfiles([]);
      setBookings([]);
      setBookingRequests([]);
      setMemberships([]);
      setServicePackages([]);
      setServices([]);
      setLessons([]);
      setTimelineItemsByUserId({});
      setFeedbackHistoryByUserId({});
      setLessonsByUserId({});
      setClientWorkspacesByUserId({});
      setRetentionActions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [
        nextProfiles,
        nextBookings,
        nextBookingRequests,
        nextMemberships,
        nextServicePackages,
        nextServices,
        nextRetentionActions,
        nextLessons,
      ] = await Promise.all([
        getOrganizationClientProfiles(db, organizationId),
        isInstructorScoped ? getInstructorBookings(db, organizationId, currentUser.uid) : getOrganizationBookings(db, organizationId, { startAfter: (() => { const d = new Date(); d.setDate(d.getDate() - 90); return d; })(), endBefore: (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d; })() }),
        isInstructorScoped ? getInstructorBookingRequests(db, currentUser.uid) : getOrganizationBookingRequests(db, organizationId),
        getOrganizationCustomerMemberships(db, organizationId),
        getOrganizationServicePackages(db, organizationId),
        getOrganizationServices(db, organizationId),
        getOrganizationRetentionActions(db, organizationId),
        // §5/§6 org-wide delivered-lesson evidence so no-Auth commercial clients surface as candidates.
        getOrganizationLessons(db, organizationId),
      ]);

      const workspaceResults = await Promise.all(
        (nextProfiles || []).map(async (profile) => {
          const normalizedProfile = normalizeClientProfile(profile);
          const workspace = await buildClientWorkspace(db, {
            organizationId,
            clientUserId: normalizedProfile.userId,
            persistProfile: false,
            services: nextServices,
          });

          return {
            userId: normalizedProfile.userId,
            workspace,
          };
        })
      );

      const nextTimelineItemsByUserId = {};
      const nextFeedbackHistoryByUserId = {};
      const nextLessonsByUserId = {};
      const nextClientWorkspacesByUserId = {};
      workspaceResults.forEach(({ userId, workspace }) => {
        nextTimelineItemsByUserId[userId] = workspace?.timeline || [];
        nextFeedbackHistoryByUserId[userId] = workspace?.feedbackHistory || [];
        nextLessonsByUserId[userId] = workspace?.lessons || [];
        nextClientWorkspacesByUserId[userId] = workspace || {};
      });

      setClientProfiles(nextProfiles || []);
      setBookings(nextBookings || []);
      setBookingRequests(nextBookingRequests || []);
      setMemberships(nextMemberships || []);
      setServicePackages(nextServicePackages || []);
      setServices(nextServices || []);
      setLessons(nextLessons || []);
      setTimelineItemsByUserId(nextTimelineItemsByUserId);
      setFeedbackHistoryByUserId(nextFeedbackHistoryByUserId);
      setLessonsByUserId(nextLessonsByUserId);
      setClientWorkspacesByUserId(nextClientWorkspacesByUserId);
      setRetentionActions(nextRetentionActions || []);
    } catch (loadError) {
      setError(loadError?.message || 'Could not load the retention workspace right now.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, isInstructorScoped, organizationId]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const retentionQueues = useMemo(() => (
    isInstructorScoped
      ? buildInstructorRetentionQueues({
          clientProfiles,
          bookings,
          memberships,
          timelineItemsByUserId,
          instructorId: currentUser?.uid || '',
        })
      : buildOrganizationRetentionQueues({
          clientProfiles,
          bookings,
          memberships,
          timelineItemsByUserId,
        })
  ) || EMPTY_RETENTION_QUEUES, [bookings, clientProfiles, currentUser?.uid, isInstructorScoped, memberships, timelineItemsByUserId]);

  const membershipsByUserId = useMemo(
    () => (memberships || []).reduce((acc, membership) => {
      const userId = membership.userId || '';
      if (!userId) return acc;
      if (!acc[userId]) acc[userId] = [];
      acc[userId].push(membership);
      return acc;
    }, {}),
    [memberships]
  );

  const bookingsByUserId = useMemo(
    () => (bookings || []).reduce((acc, booking) => {
      const userId = booking.requesterUserId || '';
      if (!userId) return acc;
      if (!acc[userId]) acc[userId] = [];
      acc[userId].push(booking);
      return acc;
    }, {}),
    [bookings]
  );

  // Pass 80 §4/§5/§6 — the canonical clientId-native, delivery-truthful retention read model. This is the
  // authority for candidacy (bookings/requests/lessons/profiles, incl. no-Auth clients) and next-best-action.
  const retentionOpportunities = useMemo(() => (
    buildOrganizationRetentionOpportunities({
      organizationId,
      bookings,
      bookingRequests,
      lessons,
      membershipsByUserId,
      services,
      retentionActions,
      clientProfiles,
      now: Date.now(),
      includeSnoozed: true, // the queue surfaces snoozed rows too; the contract flags eligibility for filtering
    }) || []
  ), [organizationId, bookings, bookingRequests, lessons, membershipsByUserId, services, retentionActions, clientProfiles]);

  const opportunitiesByClientId = useMemo(
    () => (retentionOpportunities || []).reduce((acc, opp) => {
      if (opp?.clientId) acc[opp.clientId] = opp;
      return acc;
    }, {}),
    [retentionOpportunities]
  );

  // §5/§6/§52 — surface commercial clients the legacy profile-keyed queues miss (notably no-Auth clients with
  // NO persisted profile). Each uncovered actionable opportunity is injected ONCE as a synthetic candidate row
  // into the queue that matches its deterministic next-best-action. Delivery/eligibility already decided by the
  // contract; NO_ACTION opportunities are never injected.
  const ACTION_TO_QUEUE_KEY = useMemo(() => ({
    REBOOK: 'needingRebook',
    FOLLOW_UP: 'followUpRecommended',
    REVIEW_EXISTING_REQUEST: 'followUpRecommended',
    USE_EXISTING_CREDIT: 'unusedCredits',
  }), []);

  const augmentedQueues = useMemo(() => {
    const coveredClientIds = new Set(
      (clientProfiles || []).map((profile) => clientIdOfProfile(profile)).filter(Boolean)
    );
    const next = { ...(retentionQueues || EMPTY_RETENTION_QUEUES) };
    Object.keys(EMPTY_RETENTION_QUEUES).forEach((key) => { next[key] = [...(next[key] || [])]; });
    (retentionOpportunities || []).forEach((opp) => {
      if (!opp?.clientId || coveredClientIds.has(opp.clientId)) return;
      if (opp.eligibility === 'snoozed') return; // snoozed rows are filtered out of the working queue
      const queueKey = ACTION_TO_QUEUE_KEY[opp.nextBestAction];
      if (!queueKey) return; // NO_ACTION / suppressed → not an actionable candidate row
      next[queueKey].push({
        organizationId,
        userId: opp.clientUserId || '',
        clientId: opp.clientId,
        displayName: opp.displayName || opp.clientId,
        __opportunityCandidate: true,
      });
    });
    return next;
  }, [ACTION_TO_QUEUE_KEY, clientProfiles, organizationId, retentionOpportunities, retentionQueues]);

  const riskEntries = useMemo(() => (
    getClientsAtRiskOfDropoff(clientProfiles, {
      bookingsByUserId,
      lessonsByUserId,
      feedbackHistoryByUserId,
      timelineItemsByUserId,
      membershipsByUserId,
    }) || []
  ), [bookingsByUserId, clientProfiles, feedbackHistoryByUserId, lessonsByUserId, membershipsByUserId, timelineItemsByUserId]);

  const riskMap = useMemo(
    () => (riskEntries || []).reduce((acc, entry) => {
      acc[entry.profile.userId] = entry;
      return acc;
    }, {}),
    [riskEntries]
  );

  const visibleQueueEntries = useMemo(
    () => getQueueEntries(queueType, augmentedQueues || {}),
    [queueType, augmentedQueues]
  );

  const enrichedQueueEntries = useMemo(
    () => visibleQueueEntries.map(({ queueType: activeQueueType, profile }) => {
      const recommendations = buildClientNextStepRecommendations(profile, {
        bookings: bookingsByUserId[profile.userId] || [],
        services,
        memberships: membershipsByUserId[profile.userId] || [],
        servicePackages,
        feedbackHistory: feedbackHistoryByUserId[profile.userId] || [],
      });
      const risk = riskMap[profile.userId]?.risk || null;
      const riskSignals = riskMap[profile.userId]?.signals || {};
      const membershipsForUser = membershipsByUserId[profile.userId] || [];
      const latestBooking = (bookingsByUserId[profile.userId] || [])[0] || {};
      const clientId = clientIdOfProfile(profile);
      // §25 actions are clientId-native; match on clientId, still honouring the legacy clientUserId link.
      const clientActions = (retentionActions || []).filter((action) => (
        (clientId && action.clientId === clientId)
        || (profile.userId && action.clientUserId === profile.userId)
      ));
      const workspace = clientWorkspacesByUserId[profile.userId] || {};
      return {
        queueType: activeQueueType,
        profile,
        clientId,
        opportunity: opportunitiesByClientId[clientId] || null,
        recommendations,
        risk,
        riskSignals,
        memberships: membershipsForUser,
        latestBooking,
        retentionActions: clientActions,
        workspace,
      };
    }),
    [bookingsByUserId, clientWorkspacesByUserId, feedbackHistoryByUserId, membershipsByUserId, opportunitiesByClientId, retentionActions, riskMap, servicePackages, services, visibleQueueEntries]
  );

  const filterOptions = useMemo(
    () => getRetentionQueueFilterOptions(enrichedQueueEntries) || {
      instructorIds: [],
      services: [],
      disciplines: [],
      locationTypes: [],
      membershipStatuses: [],
      riskLevels: ['high', 'medium', 'low'],
      activityWindows: ['14', '30', '60'],
      packageStatuses: ['has_balance', 'none'],
    },
    [enrichedQueueEntries]
  );

  const filteredQueueEntries = useMemo(
    () => filterRetentionQueue(enrichedQueueEntries, filters) || enrichedQueueEntries,
    [enrichedQueueEntries, filters]
  );

  const serviceFunnels = useMemo(
    () => getServiceConversionFunnel(services, {
      bookings,
      bookingRequests,
      lessons: Object.values(lessonsByUserId).flat(),
    }) || [],
    [bookings, bookingRequests, services, lessonsByUserId]
  );

  const serviceRetentionSummary = useMemo(
    () => getServiceRetentionSummary(services, bookings) || [],
    [bookings, services]
  );

  const instructorFunnel = useMemo(
    () => getInstructorConversionFunnel(isInstructorScoped ? (currentUser?.uid || '') : '', {
      bookings,
      bookingRequests,
    }) || { rebookConversionRate: 0 },
    [bookingRequests, bookings, currentUser?.uid, isInstructorScoped]
  );

  const instructorRetentionSummary = useMemo(
    () => getInstructorRetentionSummary(clientProfiles, {
      bookings,
      instructorId: isInstructorScoped ? (currentUser?.uid || '') : '',
    }) || { clientCount: 0, clientsNeedingRebook: 0 },
    [bookings, clientProfiles, currentUser?.uid, isInstructorScoped]
  );

  const followUpEffectiveness = useMemo(
    () => getFollowUpEffectivenessSummary(clientProfiles, { bookings }) || { reviewedToRebookRate: 0 },
    [bookings, clientProfiles]
  );

  const retentionActionSummary = useMemo(
    () => getRetentionActionSummary(retentionActions) || {
      totalCount: 0,
      byType: {},
      followUpSentCount: 0,
      packageOfferedCount: 0,
      rebookSuggestedCount: 0,
      snoozedCount: 0,
      reviewedCount: 0,
    },
    [retentionActions]
  );

  const serviceAttributedConversion = useMemo(
    () => getConversionByService(services, {
      bookingRequests,
      bookings,
      lessons: Object.values(lessonsByUserId).flat(),
      actions: retentionActions,
      memberships,
    }) || [],
    [bookingRequests, bookings, lessonsByUserId, memberships, retentionActions, services]
  );

  const instructorAttributedConversion = useMemo(
    () => getConversionByInstructor(isInstructorScoped ? (currentUser?.uid || '') : '', {
      bookingRequests,
      bookings,
      lessons: Object.values(lessonsByUserId).flat(),
      actions: retentionActions,
      memberships,
    }) || { conversionRate: 0 },
    [bookingRequests, bookings, currentUser?.uid, isInstructorScoped, lessonsByUserId, memberships, retentionActions]
  );

  const canViewWorkspace = canViewRetentionWorkspace(userData || {}, organizationId);
  const canManageActions = canManageRetentionActions(userData || {}, organizationId);
  const canManageBulkActions = canManageBulkRetentionActions(userData || {}, organizationId);
  const canManageBulkNotes = canManageBulkClientNotes(userData || {}, organizationId);
  const canManageBulkReassignment = canManageBulkInstructorReassignment(userData || {}, organizationId);
  const canUseDrafts = canUseAIAssistDrafts(userData || {}, organizationId);
  const canViewFunnels = canViewConversionFunnels(userData || {}, organizationId);
  const canViewReporting = canViewRetentionReporting(userData || {}, organizationId);
  const canSnooze = canSnoozeRetentionRecommendation(userData || {}, organizationId);

  useEffect(() => {
    // selection is keyed by the commercial clientId (a no-Auth client has no uid to key on)
    setSelectedClientIds((current) => current.filter((id) => filteredQueueEntries.some((entry) => entry.clientId === id)));
  }, [filteredQueueEntries]);

  const handleCreateRebook = async (profile) => {
    try {
      const recommendations = buildClientNextStepRecommendations(profile, {
        bookings: bookingsByUserId[profile.userId] || [],
        services,
        memberships: membershipsByUserId[profile.userId] || [],
        servicePackages,
        feedbackHistory: feedbackHistoryByUserId[profile.userId] || [],
      });
      const rebookResult = await createRebookRequestFromLastService(db, {
        organizationId,
        clientProfile: profile,
        clientId: clientIdOfProfile(profile),
        bookings: bookingsByUserId[profile.userId] || [],
        bookingRequests, // §34 duplicate-safe: an already-open rebook request is reused, not duplicated
        services,
        actorUserId: currentUser?.uid || '',
        actorName: userData?.name || currentUser?.email || '',
      });
      // §34 — only log a NEW action when a request was actually created (avoid inflating the audit log on a re-click).
      if (!rebookResult?.alreadyExisted) {
        await logRetentionAction(db, {
          organizationId,
          clientId: clientIdOfProfile(profile),
          clientUserId: profile.userId || '',
          instructorId: currentUser?.uid || '',
          actionType: 'rebook_request_created',
          relatedServiceId: recommendations?.nextService?.serviceId || recommendations?.nextService?.id || '',
          relatedBookingId: (bookingsByUserId[profile.userId] || [])[0]?.bookingId || '',
          note: recommendations?.rebookTiming?.message || 'Rebook request created from retention queue.',
          createdByUserId: currentUser?.uid || '',
        });
      }
      setStatusMessage(rebookResult?.alreadyExisted
        ? 'A rebook request is already open for this client.'
        : (recommendations?.rebookTiming?.message || 'Rebook request created from the retention queue.'));
      await loadWorkspace();
    } catch (actionError) {
      setError(actionError?.message || 'Could not create the rebook action.');
    }
  };

  const handleDraftFollowUp = async (profile) => {
    const recommendations = buildClientNextStepRecommendations(profile, {
      bookings: bookingsByUserId[profile.userId] || [],
      services,
      memberships: membershipsByUserId[profile.userId] || [],
      servicePackages,
      feedbackHistory: feedbackHistoryByUserId[profile.userId] || [],
    });
    const risk = riskMap[profile.userId]?.risk || null;
    const workspace = clientWorkspacesByUserId[profile.userId] || {};
    const progressSummary = buildRebookCoachingSummary({
      ...workspace,
      clientProfile: profile,
      recommendations,
    });
    const draftMessage = buildFollowUpDraftMessage(profile, {
      recommendations,
      coachingSummary: progressSummary,
    });
    const result = await openThreadWithDraft(db, {
      organizationId,
      clientProfile: profile,
      senderUserId: currentUser?.uid || '',
      senderName: userData?.name || currentUser?.email || '',
      draftMessage,
      draftType: 'followup_sent',
      templateKey: 'rebook_reminder',
      templateVars: {
        clientName: profile.displayName || '',
        instructorName: userData?.name || currentUser?.email || '',
        lastService: (bookingsByUserId[profile.userId] || [])[0]?.serviceNameSnapshot || '',
        lastLessonDate: formatDate((bookingsByUserId[profile.userId] || [])[0]?.startAt || (bookingsByUserId[profile.userId] || [])[0]?.updatedAt),
        suggestedNextService: recommendations?.nextService?.name || '',
        packageName: recommendations?.packageSuggestion?.name || '',
        progressSummary,
        nextFocus: recommendations?.coachingFocus || '',
      },
      relatedServiceId: recommendations?.nextService?.serviceId || recommendations?.nextService?.id || '',
      relatedBookingId: (bookingsByUserId[profile.userId] || [])[0]?.bookingId || '',
    });
    setDraftsByUserId((current) => ({
      ...current,
      [profile.userId]: {
        followUpPrompt: buildFollowUpDraftPrompt(profile, { recommendations, risk }),
        followUpDraftMessage: draftMessage,
        rebookExplanation: buildRebookSuggestionExplanation(profile, { recommendations, risk }),
      },
    }));
    setStatusMessage('Follow-up draft prepared in messaging.');
    navigate(result.link);
  };

  const handleDraftPackageOffer = async (profile) => {
    const recommendations = buildClientNextStepRecommendations(profile, {
      bookings: bookingsByUserId[profile.userId] || [],
      services,
      memberships: membershipsByUserId[profile.userId] || [],
      servicePackages,
      feedbackHistory: feedbackHistoryByUserId[profile.userId] || [],
    });
    const suggestion = createPackageOfferSuggestion(profile, {
      packageSuggestion: recommendations.packageSuggestion,
      recommendedService: recommendations.nextService,
    });
    const workspace = clientWorkspacesByUserId[profile.userId] || {};
    const progressSummary = buildPackageOfferCoachingSummary({
      ...workspace,
      clientProfile: profile,
      recommendations,
    });
    const draftMessage = buildPackageOfferDraftMessage(profile, {
      recommendations,
      coachingSummary: progressSummary,
    });
    const result = await openThreadWithDraft(db, {
      organizationId,
      clientProfile: profile,
      senderUserId: currentUser?.uid || '',
      senderName: userData?.name || currentUser?.email || '',
      draftMessage,
      draftType: 'package_offered',
      templateKey: 'package_offer',
      templateVars: {
        clientName: profile.displayName || '',
        instructorName: userData?.name || currentUser?.email || '',
        lastService: (bookingsByUserId[profile.userId] || [])[0]?.serviceNameSnapshot || '',
        lastLessonDate: formatDate((bookingsByUserId[profile.userId] || [])[0]?.startAt || (bookingsByUserId[profile.userId] || [])[0]?.updatedAt),
        suggestedNextService: recommendations?.nextService?.name || '',
        packageName: recommendations?.packageSuggestion?.name || '',
        progressSummary,
        nextFocus: recommendations?.coachingFocus || '',
      },
      relatedServiceId: recommendations?.nextService?.serviceId || recommendations?.nextService?.id || '',
      relatedBookingId: (bookingsByUserId[profile.userId] || [])[0]?.bookingId || '',
    });
    setDraftsByUserId((current) => ({
      ...current,
      [profile.userId]: {
        ...(current[profile.userId] || {}),
        packageOfferPrompt: buildPackageOfferDraftPrompt(profile, { recommendations }),
        packageOfferDraftMessage: draftMessage,
        packageOfferSummary: suggestion?.summary || 'No package suggestion is available for this client yet.',
      },
    }));
    setStatusMessage(suggestion ? 'Package offer draft prepared.' : 'No package offer suggestion is available right now.');
    navigate(result.link);
  };

  // §27 review/snooze state is DERIVED from the append-only retentionActions log — no dual clientProfile write.
  // This works for a no-Auth client with no persisted profile (the action is keyed by clientId).
  const handleMarkReviewed = async (profile, activeQueueType) => {
    try {
      await logRetentionAction(db, {
        organizationId,
        clientId: clientIdOfProfile(profile),
        clientUserId: profile.userId || '',
        instructorId: currentUser?.uid || '',
        actionType: 'reviewed',
        queueType: activeQueueType,
        relatedServiceId: (bookingsByUserId[profile.userId] || [])[0]?.serviceId || '',
        relatedBookingId: (bookingsByUserId[profile.userId] || [])[0]?.bookingId || '',
        note: `Retention item reviewed from ${activeQueueType} queue.`,
        createdByUserId: currentUser?.uid || '',
      });
      setStatusMessage('Retention item marked as reviewed.');
      await loadWorkspace();
    } catch (actionError) {
      setError(actionError?.message || 'Could not mark the retention item as reviewed.');
    }
  };

  const handleSnooze = async (profile, activeQueueType) => {
    try {
      const snoozedUntil = new Date();
      snoozedUntil.setDate(snoozedUntil.getDate() + 7); // §27 snooze horizon is carried on the action itself
      await logRetentionAction(db, {
        organizationId,
        clientId: clientIdOfProfile(profile),
        clientUserId: profile.userId || '',
        instructorId: currentUser?.uid || '',
        actionType: 'snoozed',
        queueType: activeQueueType,
        snoozedUntil,
        relatedServiceId: (bookingsByUserId[profile.userId] || [])[0]?.serviceId || '',
        relatedBookingId: (bookingsByUserId[profile.userId] || [])[0]?.bookingId || '',
        note: `Retention recommendation snoozed from ${activeQueueType} queue.`,
        createdByUserId: currentUser?.uid || '',
      });
      setStatusMessage('Retention recommendation snoozed for seven days.');
      await loadWorkspace();
    } catch (actionError) {
      setError(actionError?.message || 'Could not snooze the recommendation.');
    }
  };

  const selectedEntries = filteredQueueEntries.filter((entry) => selectedClientIds.includes(entry.clientId));
  const selectedPreview = getBulkActionPreview(selectedEntries, { actionType: 'bulk_ops' });

  const toggleSelectedClient = (clientId) => {
    setSelectedClientIds((current) => current.includes(clientId)
      ? current.filter((value) => value !== clientId)
      : [...current, clientId]);
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredQueueEntries.map((entry) => entry.clientId);
    const allSelected = visibleIds.every((id) => selectedClientIds.includes(id));
    setSelectedClientIds(allSelected ? [] : visibleIds);
  };

  const handleBulkFollowUp = async () => {
    if (selectedEntries.length === 0) return;
    const drafts = buildBulkFollowUpDrafts(selectedEntries, {
      buildDraft: (entry) => buildFollowUpDraftMessage(entry.profile, {
        recommendations: entry.recommendations,
        coachingSummary: buildRebookCoachingSummary({
          ...entry.workspace,
          clientProfile: entry.profile,
          recommendations: entry.recommendations,
        }),
      }),
    });
    const firstDraft = drafts[0];
    const firstEntry = selectedEntries[0];
    const firstRecommendations = firstEntry?.recommendations || {};
    const result = await openThreadWithDraft(db, {
      organizationId,
      clientProfile: firstEntry.profile,
      senderUserId: currentUser?.uid || '',
      senderName: userData?.name || currentUser?.email || '',
      draftMessage: firstDraft?.draftMessage || '',
      draftType: 'followup_sent',
      templateKey: 'rebook_reminder',
      templateVars: {
        clientName: firstEntry.profile.displayName || '',
        instructorName: userData?.name || currentUser?.email || '',
        suggestedNextService: firstRecommendations?.nextService?.name || '',
      },
      relatedServiceId: firstDraft?.relatedServiceId || '',
      relatedBookingId: firstDraft?.relatedBookingId || '',
    });
    await performBulkRetentionAction(selectedEntries.slice(1), {
      handler: async (entry) => {
        const recommendations = entry.recommendations || {};
        return openThreadWithDraft(db, {
          organizationId,
          clientProfile: entry.profile,
          senderUserId: currentUser?.uid || '',
          senderName: userData?.name || currentUser?.email || '',
          draftMessage: buildFollowUpDraftMessage(entry.profile, {
            recommendations,
            coachingSummary: buildRebookCoachingSummary({
              ...entry.workspace,
              clientProfile: entry.profile,
              recommendations,
            }),
          }),
          draftType: 'followup_sent',
          templateKey: 'rebook_reminder',
          templateVars: {
            clientName: entry.profile.displayName || '',
            instructorName: userData?.name || currentUser?.email || '',
            suggestedNextService: recommendations?.nextService?.name || '',
          },
          relatedServiceId: entry.latestBooking?.serviceId || '',
          relatedBookingId: entry.latestBooking?.bookingId || '',
        });
      },
    });
    setStatusMessage(`Prepared ${drafts.length} follow-up draft${drafts.length === 1 ? '' : 's'} and opened the first thread.`);
    navigate(result.link);
  };

  const handleBulkPackageOffer = async () => {
    if (selectedEntries.length === 0) return;
    const firstEntry = selectedEntries[0];
    const recommendations = firstEntry?.recommendations || {};
    const draftMessage = buildPackageOfferDraftMessage(firstEntry.profile, {
      recommendations,
      coachingSummary: buildPackageOfferCoachingSummary({
        ...firstEntry.workspace,
        clientProfile: firstEntry.profile,
        recommendations,
      }),
    });
    const result = await openThreadWithDraft(db, {
      organizationId,
      clientProfile: firstEntry.profile,
      senderUserId: currentUser?.uid || '',
      senderName: userData?.name || currentUser?.email || '',
      draftMessage,
      draftType: 'package_offered',
      templateKey: 'package_offer',
      templateVars: {
        clientName: firstEntry.profile.displayName || '',
        instructorName: userData?.name || currentUser?.email || '',
        suggestedNextService: recommendations?.nextService?.name || '',
        packageName: recommendations?.packageSuggestion?.name || '',
      },
      relatedServiceId: firstEntry.latestBooking?.serviceId || '',
      relatedBookingId: firstEntry.latestBooking?.bookingId || '',
    });
    setStatusMessage(`Prepared ${selectedEntries.length} package-offer draft${selectedEntries.length === 1 ? '' : 's'} and opened the first thread.`);
    navigate(result.link);
  };

  const handleBulkReviewed = async () => {
    if (selectedEntries.length === 0) return;
    await bulkMarkReviewed(db, {
      organizationId,
      entries: selectedEntries,
      reviewedByUserId: currentUser?.uid || '',
      queueType,
    });
    setStatusMessage(`Marked ${selectedEntries.length} retention item${selectedEntries.length === 1 ? '' : 's'} as reviewed.`);
    await loadWorkspace();
  };

  const handleBulkReassign = async () => {
    if (selectedEntries.length === 0 || !bulkInstructorId) return;
    await bulkAssignInstructor(db, {
      organizationId,
      entries: selectedEntries,
      instructorId: bulkInstructorId,
      assignedByUserId: currentUser?.uid || '',
    });
    setStatusMessage(`Reassigned ${selectedEntries.length} client${selectedEntries.length === 1 ? '' : 's'} to ${bulkInstructorId}.`);
    await loadWorkspace();
  };

  const handleBulkAddNote = async () => {
    if (selectedEntries.length === 0 || !bulkNoteText.trim()) return;
    await bulkAddClientNote(db, {
      organizationId,
      entries: selectedEntries,
      createdByUserId: currentUser?.uid || '',
      instructorId: currentUser?.uid || '',
      noteText: bulkNoteText.trim(),
    });
    setBulkNoteText('');
    setStatusMessage(`Added the note to ${selectedEntries.length} client${selectedEntries.length === 1 ? '' : 's'}.`);
    await loadWorkspace();
  };

  const handleBulkSnooze = async () => {
    if (selectedEntries.length === 0) return;
    await bulkSnoozeClients(db, {
      organizationId,
      entries: selectedEntries,
      snoozedByUserId: currentUser?.uid || '',
      queueType,
    });
    setStatusMessage(`Snoozed ${selectedEntries.length} retention recommendation${selectedEntries.length === 1 ? '' : 's'} for seven days.`);
    await loadWorkspace();
  };

  const fullFunnel = getFullConversionFunnel({
    bookingRequests,
    bookings,
    lessons: Object.values(lessonsByUserId).flat(),
    actions: retentionActions,
    memberships,
  });
  const trafficSourceConversion = getConversionByTrafficSource(bookingRequests, bookings);
  const followUpConversion = getFollowUpConversionRate(retentionActions, bookings);
  const packageOfferConversion = getPackageOfferConversionRate(retentionActions, memberships);
  const retentionRevenue = getRetentionRevenueSummary(retentionActions, {
    bookings,
    bookingPayments: [],
  });

  if (!canViewWorkspace) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <ProfileHeader />
        <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-950">Retention Workspace</h1>
          <p className="mt-3 text-sm text-slate-600">You do not have access to the retention workspace for this organization.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <ProfileHeader />

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Retention Workspace</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-950">
              {selectedOrganization?.organizationName || userData?.orgName || 'Organization'} Retention
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Dedicated follow-up queues, practical next actions, and explicit recommendation prompts.
            </p>
          </div>
          <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
            {isInstructorScoped
              ? `Instructor-scoped view for ${userData?.name || currentUser?.email || 'this coach'}.`
              : 'Organization-wide retention view for staff.'}
          </div>
        </div>

        {statusMessage && (
          <p className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {statusMessage}
          </p>
        )}
        {error && (
          <p className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </p>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          {Object.entries(QUEUE_CONFIG).map(([key, config]) => (
            <Link
              key={key}
              to={key === 'all' ? '/retention' : `/retention/${key}`}
              className={`rounded-full px-4 py-2 text-sm font-medium ${
                key === queueType
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {config.label}
            </Link>
          ))}
        </div>
        {canViewReporting && (
          <div className="mt-4">
            <Link to="/retention/reporting" className="text-sm font-medium text-blue-600 hover:underline">
              Open retention reporting
            </Link>
          </div>
        )}
      </section>

      <section className="mt-6 grid gap-4 md:grid-cols-4">
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Needs Rebook</p>
          <p className="mt-2 text-3xl font-semibold text-slate-950">{retentionQueues.needingRebook.length}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Inactive Risk</p>
          <p className="mt-2 text-3xl font-semibold text-slate-950">{riskEntries.filter((entry) => entry.risk.level === 'high').length}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Unused Credits</p>
          <p className="mt-2 text-3xl font-semibold text-slate-950">{retentionQueues.unusedCredits.length}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Reviewed to Rebook</p>
          <p className="mt-2 text-3xl font-semibold text-slate-950">{Math.round((followUpEffectiveness.reviewedToRebookRate || 0) * 100)}%</p>
        </article>
      </section>

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">{(QUEUE_CONFIG[queueType] || QUEUE_CONFIG.all).label}</h2>
            <p className="mt-2 text-sm text-slate-600">{(QUEUE_CONFIG[queueType] || QUEUE_CONFIG.all).description}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
            {filteredQueueEntries.length} client{filteredQueueEntries.length === 1 ? '' : 's'} in view
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-4 xl:grid-cols-8">
          <select value={filters.instructorId} onChange={(e) => setFilters((current) => ({ ...current, instructorId: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">All instructors</option>
            {filterOptions.instructorIds.map((instructorId) => <option key={instructorId} value={instructorId}>{instructorId}</option>)}
          </select>
          <select value={filters.serviceId} onChange={(e) => setFilters((current) => ({ ...current, serviceId: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">All services</option>
            {filterOptions.services.map((service) => <option key={service.value} value={service.value}>{service.label}</option>)}
          </select>
          <select value={filters.packageStatus} onChange={(e) => setFilters((current) => ({ ...current, packageStatus: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">All package states</option>
            {filterOptions.packageStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <select value={filters.membershipStatus} onChange={(e) => setFilters((current) => ({ ...current, membershipStatus: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">All membership states</option>
            {filterOptions.membershipStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <select value={filters.riskLevel} onChange={(e) => setFilters((current) => ({ ...current, riskLevel: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">All risk levels</option>
            {filterOptions.riskLevels.map((level) => <option key={level} value={level}>{level}</option>)}
          </select>
          <select value={filters.lastActivityWindow} onChange={(e) => setFilters((current) => ({ ...current, lastActivityWindow: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">Any activity window</option>
            {filterOptions.activityWindows.map((windowValue) => <option key={windowValue} value={windowValue}>{windowValue}+ days inactive</option>)}
          </select>
          <select value={filters.discipline} onChange={(e) => setFilters((current) => ({ ...current, discipline: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">All disciplines</option>
            {filterOptions.disciplines.map((discipline) => <option key={discipline} value={discipline}>{discipline}</option>)}
          </select>
          <select value={filters.locationType} onChange={(e) => setFilters((current) => ({ ...current, locationType: e.target.value }))} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            <option value="">All locations</option>
            {filterOptions.locationTypes.map((locationType) => <option key={locationType} value={locationType}>{locationType}</option>)}
          </select>
        </div>

        {canManageBulkActions && filteredQueueEntries.length > 0 && (
          <div className="mt-4 rounded-2xl bg-slate-50 p-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={filteredQueueEntries.length > 0 && filteredQueueEntries.every((entry) => selectedClientIds.includes(entry.clientId))}
                      onChange={toggleSelectAllVisible}
                    />
                    Select all in view
                  </label>
                  <span>{selectedEntries.length} selected</span>
                  {selectedPreview.clientCount > 0 && (
                    <span>
                      Across {selectedPreview.queueTypes.length || 1} queue{selectedPreview.queueTypes.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={handleBulkFollowUp} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
                    Bulk Follow-Up
                  </button>
                  <button type="button" onClick={handleBulkPackageOffer} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
                    Bulk Package Offer
                  </button>
                  <button type="button" onClick={handleBulkReviewed} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
                    Bulk Review
                  </button>
                  <button type="button" onClick={handleBulkSnooze} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
                    Bulk Snooze
                  </button>
                </div>
              </div>
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto_auto]">
                <input
                  type="text"
                  value={bulkNoteText}
                  onChange={(e) => setBulkNoteText(e.target.value)}
                  placeholder="Add a bulk internal note"
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
                {canManageBulkReassignment && (
                  <select
                    aria-label="Bulk instructor assignment"
                    value={bulkInstructorId}
                    onChange={(e) => setBulkInstructorId(e.target.value)}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="">Assign instructor</option>
                    {filterOptions.instructorIds.map((instructorId) => (
                      <option key={instructorId} value={instructorId}>{instructorId}</option>
                    ))}
                  </select>
                )}
                <div className="flex flex-wrap gap-2">
                  {canManageBulkNotes && (
                    <button type="button" onClick={handleBulkAddNote} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
                      Bulk Add Note
                    </button>
                  )}
                  {canManageBulkReassignment && (
                    <button type="button" onClick={handleBulkReassign} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">
                      Bulk Reassign
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <p className="mt-6 text-sm text-slate-600">Loading retention queues...</p>
        ) : filteredQueueEntries.length === 0 ? (
          <p className="mt-6 text-sm text-slate-600">No clients are currently in this queue.</p>
        ) : (
          <div className="mt-6 space-y-4">
            {filteredQueueEntries.map(({ queueType: activeQueueType, profile, clientId, recommendations, risk, riskSignals, retentionActions: clientRetentionActions, workspace }) => {
              const draftState = draftsByUserId[profile.userId] || {};

              return (
                <article key={`${activeQueueType}:${clientId}`} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {canManageBulkActions && (
                          <input
                            type="checkbox"
                            aria-label={`Select ${profile.displayName}`}
                            checked={selectedClientIds.includes(clientId)}
                            onChange={() => toggleSelectedClient(clientId)}
                          />
                        )}
                        <h3 className="text-lg font-semibold text-slate-950">{profile.displayName}</h3>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600">
                          {QUEUE_CONFIG[activeQueueType]?.label || 'Queue'}
                        </span>
                        {risk?.level && (
                          <span className={`rounded-full px-3 py-1 text-xs font-medium ${
                            risk.level === 'high'
                              ? 'bg-rose-100 text-rose-700'
                              : risk.level === 'medium'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {risk.level} retention risk
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">
                        {buildRebookSuggestionExplanation(profile, {
                          recommendations,
                          risk,
                        })}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                        <span>Last activity: {formatDate(riskSignals.lastActivityAt || profile.lastBookingAt || profile.lastLessonAt)}</span>
                        <span>Remaining sessions: {riskSignals.remainingSessions || 0}</span>
                        <span>Remaining credits: {riskSignals.remainingCredits || 0}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={clientIdOfProfile(profile) && organizationId
                          ? `/org/${organizationId}/clients/${encodeURIComponent(clientIdOfProfile(profile))}`
                          : `/clients/${profile.userId}`}
                        className="rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white"
                      >
                        Open CRM
                      </Link>
                      {/* §57/§71 direct messaging needs an Auth-linked participant; a no-Auth commercial client has no thread. */}
                      {profile.userId ? (
                        <Link
                          to={`/notifications?tab=messages&clientUserId=${encodeURIComponent(profile.userId)}`}
                          className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200"
                        >
                          Send Message
                        </Link>
                      ) : (
                        <span className="rounded-full bg-slate-50 px-4 py-2 text-sm font-medium text-slate-400 ring-1 ring-slate-200" title="This client has no linked app account yet.">
                          Message unavailable
                        </span>
                      )}
                      {canManageActions && (
                        <button
                          type="button"
                          onClick={() => handleCreateRebook(profile)}
                          className="rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white"
                        >
                          Create Rebook
                        </button>
                      )}
                      {canUseDrafts && (
                        <button
                          type="button"
                          onClick={() => handleDraftFollowUp(profile)}
                          className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200"
                        >
                          Compose Follow-Up
                        </button>
                      )}
                      {canUseDrafts && (
                        <button
                          type="button"
                          onClick={() => {
                            const workspace = clientWorkspacesByUserId[profile.userId] || {};
                            const signals = buildClientSignalsFromWorkspace(workspace, organizationId, { services });
                            setAiFollowUpModal({ profile, signals });
                          }}
                          className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
                        >
                          AI Draft
                        </button>
                      )}
                      {canUseDrafts && (
                        <button
                          type="button"
                          onClick={() => handleDraftPackageOffer(profile)}
                          className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200"
                        >
                          Compose Package Offer
                        </button>
                      )}
                      {canManageActions && (
                        <button
                          type="button"
                          onClick={() => handleMarkReviewed(profile, activeQueueType)}
                          className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200"
                        >
                          Mark Reviewed
                        </button>
                      )}
                      {canSnooze && (
                        <button
                          type="button"
                          onClick={() => handleSnooze(profile, activeQueueType)}
                          className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200"
                        >
                          Snooze 7 Days
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <div className="rounded-2xl bg-white p-4">
                      <h4 className="text-sm font-semibold text-slate-900">Next Actions</h4>
                      <ul className="mt-3 space-y-2 text-sm text-slate-600">
                        <li>Suggested service: {recommendations?.nextService?.name || 'No clear service recommendation yet.'}</li>
                        <li>Rebook timing: {recommendations?.rebookTiming?.message || 'No timing recommendation yet.'}</li>
                        <li>Package opportunity: {recommendations?.packageSuggestion?.name || 'No package recommendation right now.'}</li>
                        <li>Coaching focus: {recommendations?.coachingFocus || 'No coaching focus suggestion yet.'}</li>
                        <li>Progress summary: {buildClientProgressSummary({
                          ...workspace,
                          clientProfile: profile,
                          recommendations,
                        })}</li>
                      </ul>
                    </div>
                    <div className="rounded-2xl bg-white p-4">
                      <h4 className="text-sm font-semibold text-slate-900">Drafts and Explanation</h4>
                      <div className="mt-3 space-y-3 text-sm text-slate-600">
                        <p><span className="font-semibold text-slate-900">Why this suggestion:</span> {buildRebookSuggestionExplanation(profile, { recommendations, risk })}</p>
                        {draftState.followUpPrompt && (
                          <p><span className="font-semibold text-slate-900">Follow-up draft prompt:</span> {draftState.followUpPrompt}</p>
                        )}
                        {draftState.packageOfferPrompt && (
                          <p><span className="font-semibold text-slate-900">Package offer draft prompt:</span> {draftState.packageOfferPrompt}</p>
                        )}
                        {draftState.packageOfferSummary && (
                          <p><span className="font-semibold text-slate-900">Package offer note:</span> {draftState.packageOfferSummary}</p>
                        )}
                        {draftState.followUpDraftMessage && (
                          <p><span className="font-semibold text-slate-900">Composed follow-up:</span> {draftState.followUpDraftMessage}</p>
                        )}
                        {draftState.packageOfferDraftMessage && (
                          <p><span className="font-semibold text-slate-900">Composed package offer:</span> {draftState.packageOfferDraftMessage}</p>
                        )}
                        <p><span className="font-semibold text-slate-900">Coaching summary:</span> {buildRebookCoachingSummary({
                          ...workspace,
                          clientProfile: profile,
                          recommendations,
                        })}</p>
                        {clientRetentionActions[0] && (
                          <p>
                            <span className="font-semibold text-slate-900">Last retention action:</span>{' '}
                            {clientRetentionActions[0].note || clientRetentionActions[0].actionType} on {formatDate(clientRetentionActions[0].createdAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {canViewFunnels && (
        <section className="mt-6 grid gap-6 lg:grid-cols-2">
          <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Service Conversion Funnel</h2>
            <div className="mt-4 space-y-3 text-sm text-slate-600">
              {serviceFunnels.length === 0 ? (
                <p>No service funnel data is available yet.</p>
              ) : (
                serviceFunnels.slice(0, 4).map((service) => (
                  <div key={service.serviceId} className="rounded-2xl bg-slate-50 p-4">
                    <p className="font-semibold text-slate-900">{service.serviceName}</p>
                    <p>Requests: {service.bookingRequestCount} | Confirmed: {service.confirmedBookingCount} | Completed bookings: {service.completedBookingCount} | Delivered lessons: {service.deliveredLessonCount}</p>
                    <p>Rebook rate: {Math.round((service.rebookRate || 0) * 100)}% | Package conversion: {Math.round((service.packageConversionRate || 0) * 100)}%</p>
                  </div>
                ))
              )}
            </div>
          </article>

          <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Retention Funnel Summary</h2>
            <div className="mt-4 space-y-3 text-sm text-slate-600">
              <p>Retention actions logged: {retentionActionSummary.totalCount || 0}</p>
              <p>Follow-up messages sent: {retentionActionSummary.followUpSentCount || 0}</p>
              <p>Package offers sent: {retentionActionSummary.packageOfferedCount || 0}</p>
              {canViewReporting && (
                <>
                  <p>Rebooks from follow-ups: {followUpConversion.rebookedAfterFollowUpCount || 0}</p>
                  <p>Packages sold from offers: {packageOfferConversion.purchasesAfterOfferCount || 0}</p>
                  <p>Revenue from retention: {retentionRevenue.attributableRevenue || 0}</p>
                </>
              )}
              <p>Reviewed to rebook rate: {Math.round((followUpEffectiveness.reviewedToRebookRate || 0) * 100)}%</p>
              <p>Instructor clients: {instructorRetentionSummary.clientCount}</p>
              <p>Instructor rebook-ready clients: {instructorRetentionSummary.clientsNeedingRebook}</p>
              <p>Rebook conversion rate: {Math.round((instructorFunnel.rebookConversionRate || 0) * 100)}%</p>
              <p>Follow-up to rebook: {Math.round((instructorAttributedConversion.conversionRate || 0) * 100)}%</p>
              <p>Full funnel, public to booking: {Math.round((fullFunnel.publicToBooking?.conversionRate || 0) * 100)}%</p>
              <p>Full funnel, lesson to rebook: {Math.round((fullFunnel.lessonToRebook?.conversionRate || 0) * 100)}%</p>
              {trafficSourceConversion[0] && (
                <p>Top traffic source: {trafficSourceConversion[0].sourceType} at {Math.round((trafficSourceConversion[0].conversionRate || 0) * 100)}%</p>
              )}
              {serviceRetentionSummary[0] && (
                <p>Leading service retention signal: {serviceRetentionSummary[0].serviceName} at {Math.round((serviceRetentionSummary[0].rebookRate || 0) * 100)}% rebook rate.</p>
              )}
              {serviceAttributedConversion[0] && (
                <p>Attributed package-offer conversion for {serviceAttributedConversion[0].serviceName}: {Math.round((serviceAttributedConversion[0].conversionRate || 0) * 100)}%</p>
              )}
            </div>
          </article>
        </section>
      )}

      {aiFollowUpModal ? (
        <AiCopilotPanel
          draftType="follow_up_message"
          signals={aiFollowUpModal.signals}
          clientName={aiFollowUpModal.profile.displayName || 'Client'}
          applyFn={async (draftText) => {
            const profile = aiFollowUpModal.profile;
            const result = await openThreadWithDraft(db, {
              organizationId,
              clientProfile: profile,
              senderUserId: currentUser?.uid || '',
              senderName: userData?.name || currentUser?.email || '',
              draftMessage: draftText.trim(),
              draftType: 'followup_sent',
              templateKey: 'ai_followup',
              templateVars: {
                clientName: profile.displayName || '',
                instructorName: userData?.name || currentUser?.email || '',
              },
            });
            await logRetentionAction(db, {
              organizationId,
              clientId: clientIdOfProfile(profile),
              clientUserId: profile.userId || '',
              instructorId: currentUser?.uid || '',
              actionType: 'followup_sent',
              note: 'Follow-up sent via AI copilot draft.',
              createdByUserId: currentUser?.uid || '',
            });
            setStatusMessage('AI-assisted follow-up sent.');
            if (result?.link) navigate(result.link);
          }}
          onDone={() => setAiFollowUpModal(null)}
          onDiscard={() => setAiFollowUpModal(null)}
        />
      ) : null}
    </div>
  );
}

export default RetentionQueuePage;
